'use server'

import { apiRequest, ApiResponse } from '../request'
import { BillingAttachment } from '../common/interfaces/billing.interface'

export type BillingApprovalStatus = 'NOT_REQUIRED' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED'

/**
 * Filial dona do registro.
 *
 * A fila é da ORGANIZAÇÃO: a mesma tela mistura unidades, e sem isto duas contas
 * de valor igual ficam indistinguíveis. A UI só destaca quando a filial é
 * diferente da que o usuário está usando.
 */
export interface ApprovalCompany {
    id: number
    name: string
    tradingName?: string | null
}

export interface BillingApprovalItem {
    company?: ApprovalCompany | null
    /**
     * Metadados do lançamento. A fila lê `paymentSplitCount` daqui para avisar que o
     * pagamento foi dividido em formas — sem precisar carregar as filhas de cada
     * solicitação só para contar.
     */
    metadata?: Record<string, any> | null
    id: number
    amount: number
    description: string | null
    dueDate: string
    approvalStatus: BillingApprovalStatus
    createdAt: string
    supplier?: { id: number; name: string; cnpj: string | null } | null
    partner?: { id: number; name: string } | null
    /** Repasse ao profissional: é ele o destinatário exibido na linha. */
    professional?: { id: number; name: string } | null
    // Ausente quando a conta nasceu de um fluxo automático (ex: repasse ao parceiro
    // gerado pelo payout orchestrator) — nesses casos não há solicitante humano.
    createdBy?: { id: number; name: string } | null
    category?: string | null
    /**
     * Como a conta será paga. Decide o que "Aprovar" faz: PIX e TED saem pela conta
     * digital; dinheiro, maquininha e "outro" são pagos por fora e a aprovação apenas
     * registra a baixa. O aprovador precisa ver isso antes de clicar.
     */
    paymentMethod?: string | null

    /**
     * Solicitação AGRUPADA de repasse: soma vários lançamentos de um destinatário
     * num período. O valor exibido é a soma — daí o "Ver grupo", que abre a
     * composição antes de aprovar.
     */
    isPayoutBatch?: boolean

    /**
     * Anexos do lançamento. No repasse agrupado é onde fica o **demonstrativo em
     * PDF** gerado na criação — o documento que o operador confere e que é enviado
     * ao destinatário.
     */
    attachments?: BillingAttachment[] | null

    /**
     * Destinatário e prontidão para transferência, calculados pelo backend com a
     * mesma regra que o `TransferService` usa. `null` quando o pagamento não depende
     * do cadastro do destinatário (conta bancária vinculada ao lançamento, RH).
     */
    transferRecipient?: {
        kind: 'PARTNER' | 'PROFESSIONAL' | 'SUPPLIER'
        id: number
        canTransfer: boolean
    } | null

    /** Marcada como já paga por fora da conta digital (aprovar não transfere). */
    externalTransferVia?: string | null
    externalTransferAt?: string | null
    externalTransferBy?: { id: number; name: string } | null
}

const BASE = 'financial/billing-approvals'

/**
 * Fila inteira de contas a pagar pendentes (a paginação é no cliente).
 *
 * `search` é resolvido no servidor porque o que o operador procura nem sempre está
 * na linha: numa solicitação AGRUPADA, o parceiro está no lote mas o paciente e o
 * número do repasse estão nos ITENS dele — filtrar no navegador só acharia o que já
 * está na tela.
 */
export async function getPendingBillingApprovals(
    search?: string,
): Promise<ApiResponse<BillingApprovalItem[]>> {
    return apiRequest<BillingApprovalItem[]>(BASE, {
        params: search ? { search } : {},
    })
}

/** Página de histórico de aprovações (genérico, reutilizado pelas 3 filas). */
export interface ApprovedPage<T> {
    items: T[]
    total: number
    page: number
    limit: number
}

export type ApprovedBillingApprovalItem = BillingApprovalItem & {
    approvedBy?: { id: number; name: string } | null
    approvedAt?: string | null
}

export async function getApprovedBillingApprovals(
    page = 1,
    limit = 20,
    search?: string,
): Promise<ApiResponse<ApprovedPage<ApprovedBillingApprovalItem>>> {
    return apiRequest(`${BASE}/history`, {
        params: { page, limit, ...(search ? { search } : {}) },
    })
}

export async function approveBilling(
    id: number,
    password: string,
): Promise<ApiResponse<BillingApprovalItem>> {
    return apiRequest<BillingApprovalItem>(
        `${BASE}/${id}/approve`,
        { data: { password } },
        'post',
    )
}

export async function rejectBilling(id: number, reason: string): Promise<ApiResponse<BillingApprovalItem>> {
    return apiRequest<BillingApprovalItem>(`${BASE}/${id}/reject`, { data: { reason } }, 'post')
}

/**
 * Tira a solicitação da fila sem decidir nada — para o pedido aberto por engano.
 *
 * No repasse agrupado, o backend solta os repasses de volta ao pool e apaga a conta
 * agrupada: o financeiro corrige o recorte e gera outra. Numa conta a pagar comum,
 * apenas sai da fila (ali o lançamento é o próprio repasse do parceiro).
 *
 * Exige `delete` em `financial:billing_approvals` **e** ser o solicitante ou operador
 * financeiro — o backend recusa nos demais casos, e também quando a conta já foi paga
 * ou já tem transferência.
 */
export async function removeBillingApproval(
    id: number,
): Promise<ApiResponse<{ removed: boolean; wasPayoutBatch: boolean }>> {
    return apiRequest<{ removed: boolean; wasPayoutBatch: boolean }>(
        `${BASE}/${id}`,
        {},
        'delete',
    )
}

/**
 * Resultado de um lote. O backend processa item a item e NÃO aborta na primeira
 * falha: os ids em `succeeded` já foram efetivados (aprovar conta a pagar pode ter
 * disparado transferência). Um lote parcial volta com HTTP 200 — quem chama precisa
 * olhar `failed`, nunca só o `success` da resposta.
 */
export interface BulkResult {
    succeeded: number[]
    failed: { id: number; error: string }[]
}

/**
 * O que fazer com o dinheiro ao aprovar a conta a pagar:
 * - `TRANSFER`: transfere pela conta digital (PIX/TED via provedor);
 * - `APPROVE_ONLY`: só aprova — nada é movimentado, a baixa vem depois;
 * - `ALREADY_TRANSFERRED`: o financeiro já pagou por fora; marca como paga e
 *   registra a instituição, sem tocar na conta digital.
 */
export type ApprovePayableMode =
    | 'TRANSFER'
    | 'APPROVE_ONLY'
    | 'ALREADY_TRANSFERRED'

export interface ApprovePayableOptions {
    mode: ApprovePayableMode
    /** Obrigatória em ALREADY_TRANSFERRED. */
    transferredVia?: string
}

/**
 * Marca a conta a pagar como já transferida por fora (outro banco/instituição).
 * Exige apenas permissão de LEITURA da fila — quem pagou nem sempre é quem aprova.
 * Marcada assim, a aprovação do operador não dispara transferência.
 */
export async function markBillingExternallyTransferred(
    billingId: number,
    transferredVia: string,
): Promise<ApiResponse<BillingApprovalItem>> {
    return apiRequest<BillingApprovalItem>(
        `${BASE}/${billingId}/external-transfer`,
        { data: { transferredVia } },
        'post'
    )
}

export async function clearBillingExternalTransfer(
    billingId: number,
): Promise<ApiResponse<BillingApprovalItem>> {
    return apiRequest<BillingApprovalItem>(
        `${BASE}/${billingId}/external-transfer`,
        {},
        'delete'
    )
}

export async function bulkApproveBillings(
    ids: number[],
    password: string,
    options?: ApprovePayableOptions,
): Promise<ApiResponse<BulkResult>> {
    return apiRequest<BulkResult>(
        `${BASE}/bulk-approve`,
        { data: { ids, password, ...(options || {}) } },
        'post'
    )
}

export async function bulkRejectBillings(
    ids: number[],
    reason: string,
): Promise<ApiResponse<BulkResult>> {
    return apiRequest<BulkResult>(`${BASE}/bulk-reject`, { data: { ids, reason } }, 'post')
}
